import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import MarketingLayout from "@/components/marketing/MarketingLayout";

const TABS = [
  { id: "privacy", label: "Privacy Policy" },
  { id: "terms", label: "Terms of Service" },
  { id: "cookies", label: "Cookie Policy" },
];

const PRIVACY = `
WYLO Privacy Policy

Lawful basis for processing:
- Legitimate interests (employer use of WYLO Sync / Momentum / Command)
- Consent (individual use of WYLO Spark / Focus Web / Focus)

Data we collect:
- Account: name, email, role, organisation domain
- Derived behavioural signals: cursor velocity score, keystroke cadence score, app/website focus events (priority rank only), FACE event metadata
- Hashed URLs (one-way, never readable)
- Audit logs of administrative actions

Data we do NOT collect:
- Raw keystroke content
- Readable URLs
- Screen captures or recordings (the "Coming Soon: Screen Replay" toggle is non-active at launch)
- Personal communications

Data subject rights (GDPR Articles 15–22):
- Access, rectify, export and delete your data from within Settings
- Deletion requests fulfilled within 24 hours by S-Tier

Data Protection Officer:
- dpo@usewylo.com (to be staffed prior to public launch)

International transfers:
- WYLO data is stored within the EU (Ireland or EU-based cloud region).

EU AI Act transparency:
- AI features are clearly labelled. You may opt out of behavioural AI modelling without losing access to the base product.

Retention:
- Individual: duration of account + 30 days post-deletion
- Enterprise workspace: duration of contract + 30 days post-cancellation
`;

const TERMS = `
WYLO Terms of Service

Acceptable use:
- You will not use WYLO to surveil individuals or punish workers for normal cognitive patterns.
- Aggregate insights are for legitimate operational decisions only.

Account responsibilities:
- You agree to keep credentials secure.
- Admin/SuperAdmin accounts accept responsibility for the use of any Consultant accounts granted access.

Tier-specific terms:
- WYLO Spark: 14-day free trial. Anti-abuse logic applies to "+" aliases and disposable email domains.
- Sync/Momentum/Command: business documentation may be required for non-corporate domains.

Data ownership:
- You own your data. WYLO stores and processes it on your behalf.

Liability limitations:
- Provided "as is" with reasonable care. Aggregated and anonymised analytics are not a substitute for occupational health or HR advice.

Governing law:
- Republic of Ireland.

Dispute resolution:
- Initial informal resolution; arbitration in Dublin under Irish arbitration law.
`;

const COOKIES = `
WYLO Cookie Policy

Categories:
- Necessary (always on): session, authentication, security
- Analytics (opt-in): anonymised usage analytics
- Marketing (opt-in): communications tailoring
- Functional (opt-in): UI preferences, sidebar state

You can change your preferences any time from the footer "Cookie Settings" link.
This policy operates in conjunction with the ePrivacy Directive and the GDPR.
`;

const BODIES = { privacy: PRIVACY, terms: TERMS, cookies: COOKIES };

export default function Legal() {
  const { doc = "privacy" } = useParams();
  const navigate = useNavigate();
  const body = BODIES[doc] || PRIVACY;

  return (
    <MarketingLayout>
      <section className="pt-28 pb-8 max-w-5xl mx-auto px-6 lg:px-10">
        <p className="text-xs uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold mb-4">Legal</p>
        <h1 className="font-display text-5xl lg:text-6xl tracking-tighter text-[#1A1A2E] leading-[0.95]">
          {TABS.find((t) => t.id === doc)?.label || "Privacy Policy"}
        </h1>
        <div className="mt-6 inline-flex bg-white border border-[#E8ECF2] rounded-full p-1">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => navigate(`/legal/${t.id}`)}
              className={`text-sm px-4 py-1.5 rounded-full transition-colors ${
                doc === t.id ? "bg-[#1A3A5C] text-white" : "text-[#555568] hover:text-[#1A1A2E]"
              }`}
              data-testid={`legal-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 lg:px-10 pb-32">

        <pre className="whitespace-pre-wrap font-sans text-[15px] text-[#1A1A2E] leading-relaxed">
          {body.trim().split(/([\w.+-]+@usewylo\.com)/g).map((part, i) =>
            /@usewylo\.com$/.test(part)
              ? <a key={i} href={`mailto:${part}`} className="text-[#2C6BAC] hover:underline">{part}</a>
              : part
          )}
        </pre>
      </section>
    </MarketingLayout>
  );
}
